import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

import Search from 'components/Search'

import DeviceAggregatorTabs from 'pages/DeviceAggregator/Tabs'

import { useServerErrorContext } from 'hooks/ServerErrorContext'

import { isDeviceIdValid, isHEHMDeviceIdValid } from 'helpers/validations'

import { screenSize, PageHeader, PageTitle, SinglePanePage } from 'styles/shared'

import Api from 'services/api'

import { DeviceBase } from 'types/Device'

const DeviceAggregator = () => {
  const [deviceId, setDeviceId] = useState('')
  const [searchInProgress, setSearchInProgress] = useState(false)
  const [device, setDevice] = useState<DeviceBase | null>(null)
  const [error, setError] = useState('')

  const { setServerError } = useServerErrorContext()

  const onClickSearch = useCallback(async () => {
    if (!isDeviceIdValid(deviceId) && !isHEHMDeviceIdValid(deviceId)) {
      setError('Invalid Device AUID or Serial Number')

      return
    }

    if (device?.deviceId === deviceId) {
      return // device already loaded
    }

    setSearchInProgress(true)

    await Api.Devices.search([deviceId])
      .then(res => {
        if (res?.devices?.length) {
          setDevice(res.devices[0])
        }

        setError(res?.invalidDeviceIds?.length ? 'Device Not Found' : '')
      }).catch(() => {
        setServerError('Failed to get device')
        setDevice(null)
        setError('')
      })
      .finally(() => {
        setSearchInProgress(false)
      })
  }, [device, deviceId, setServerError])

  const onChangeDeviceId = (id: string) => {
    if (id.trim() !== deviceId) {
      setDeviceId(id.trim())
      setDevice(null)
      setError('')
    }
  }

  return (
    <>
      <S.PageHeader>
        <PageTitle>Device Aggregator</PageTitle>
        <S.Search
          value={deviceId}
          placeholder="Device AUID or Serial Number"
          ariaLabel="Device AUID or Serial Number"
          onChange={(value) => onChangeDeviceId(value.toUpperCase())}
          onSearch={onClickSearch}
          onClickClearIcon={() => onChangeDeviceId('')}
          showSpinner={searchInProgress}
          errorMessage={error}
        />
      </S.PageHeader>
      <SinglePanePage>
        {device && <DeviceAggregatorTabs deviceId={device.deviceId} timeZone={device.timeZone} deviceType={device.deviceType} />}
      </SinglePanePage>
    </>
  )
}

export default DeviceAggregator

const S = {
  PageHeader: styled(PageHeader)`
    @media ${screenSize.small} {
      padding-right: 25px;
      margin-top: -5px;
      padding: 20px;
      margin-bottom: 0;
    }
  `,
  TopContainer: styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 10px;

    @media ${screenSize.small} {
      flex-direction: column;
    }
  `,
  Search: styled(Search)`
    width: 500px;

    @media ${screenSize.small} {
      display: block;
      width: 100%;
    }
  `
}
